import { inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CORE_AUTH_ENDPOINTS } from '../models';
import {
  AuthFlow,
  AuthSessionSnapshot,
  AuthTokenSet,
  AuthTokens,
  ForgetPasswordRequest,
  LoginRequest,
  RegisterRequest,
} from '../models';
import { AUTH_TOKENS_STORAGE_KEY, AUTH_PERSIST_KEY } from '../tokens';
import {
  Observable,
  tap,
  catchError,
  of,
  map,
  from,
  switchMap,
  firstValueFrom,
} from 'rxjs';
import { Cookie } from './cookie';
import { HttpRestService } from './http-rest';
import { RecaptchaService } from './recaptcha';
import { APP_CONFIG, RECAPTCHA_ACTIONS } from '@core/config';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private readonly _http = inject(HttpRestService);
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _appConfig = inject(APP_CONFIG);
  private readonly _cookieService = inject(Cookie);
  private readonly _recaptcha = inject(RecaptchaService);
  readonly session = signal<AuthSessionSnapshot>({
    isAuthenticated: false,
    hasOtpContext: false,
    user: null,
  });

  readonly invalidation = signal(0);

  private _session = this.session;

  private _refreshPromise: Promise<AuthTokens | null> | null = null;

  snapshot(): AuthSessionSnapshot {
    return this._session();
  }

  register(request: RegisterRequest): Observable<void> {
    this.savePersistFlag(true);
    return this._http
      .post<AuthTokens>(
        CORE_AUTH_ENDPOINTS.register,
        request,
        this.ـauthHeaders(),
      )
      .pipe(
        tap((tokens) => this._handleSuccessfulAuthentication(tokens)),
        map(() => void 0),
      );
  }

  login(request: LoginRequest): Observable<AuthTokens> {
    return from(this._recaptcha.execute(RECAPTCHA_ACTIONS.login)).pipe(
      map((token) => ({ ...request, reCAPTCHA: token })),
      switchMap((body) =>
        this._http.post<AuthTokens>(
          CORE_AUTH_ENDPOINTS.login,
          body,
          this.ـauthHeaders(),
        ),
      ),
    );
  }

  completeAuthentication(tokens: AuthTokenSet): void {
    this._handleSuccessfulAuthentication(tokens);
  }

  logout(): Observable<void> {
    const tokens = this.restoreTokens();
    const body = tokens?.refreshToken
      ? { refreshToken: tokens.refreshToken }
      : {};
    return this._http.post<void>(CORE_AUTH_ENDPOINTS.logout, body).pipe(
      tap(() => {
        this.clearTokens();
        this._router.navigate(['/auth/signin']);
      }),
      catchError(() => {
        this.clearTokens();
        this._router.navigate(['/auth/signin']);
        return of(void 0);
      }),
    );
  }

  forgetPassword(request: ForgetPasswordRequest): Observable<void> {
    return this._http
      .post<AuthTokens>(
        CORE_AUTH_ENDPOINTS.forgetPassword,
        request,
        this.ـauthHeaders(),
      )
      .pipe(
        tap((tokens) => this._handleSuccessfulAuthentication(tokens)),
        map(() => void 0),
      );
  }

  saveTokens(tokens: AuthTokenSet): void {
    const essentialTokens = {
      accessToken: tokens.accessToken,
      tokenType: tokens.tokenType,
      expiresIn: tokens.expiresIn,
      refreshToken: tokens.refreshToken,
    };

    this._cookieService.setCookie(
      AUTH_TOKENS_STORAGE_KEY,
      JSON.stringify(essentialTokens),
      7,
    );
    this._session.update((value) => ({
      ...value,
      isAuthenticated: true,
      user: tokens,
    }));
  }

  authHeader(tokens: {
    accessToken: string;
    tokenType?: string | null;
  }): string {
    const scheme = /^[\w-]+$/.test(tokens.tokenType ?? '')
      ? tokens.tokenType
      : 'Bearer';

    return `${scheme} ${tokens.accessToken}`;
  }

  restoreTokens(): Partial<AuthTokens> | null {
    const raw = this._cookieService.getCookie(AUTH_TOKENS_STORAGE_KEY);

    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch {
      this._cookieService.deleteCookie(AUTH_TOKENS_STORAGE_KEY);
      return null;
    }
  }

  savePersistFlag(remember: boolean): void {
    this._cookieService.setCookie(AUTH_PERSIST_KEY, String(remember), {
      days: 365,
      sameSite: 'Strict',
    });
  }

  getPersistFlag(): boolean {
    const raw = this._cookieService.getCookie(AUTH_PERSIST_KEY);
    return raw === 'true';
  }

  clearPersistFlag(): void {
    this._cookieService.deleteCookie(AUTH_PERSIST_KEY);
  }

  clearTokens(): void {
    this._cookieService.deleteCookie(AUTH_TOKENS_STORAGE_KEY);
    this._session.set({
      isAuthenticated: false,
      hasOtpContext: false,
      user: null,
    });
    this.invalidation.update((count) => count + 1);
  }

  getReturnUrlQueryParam(url: string): { returnUrl: string } | null {
    const returnUrl = this._safeReturnUrl(url);

    return returnUrl ? { returnUrl } : null;
  }

  consumeReturnUrl(): string | null {
    return this._safeReturnUrl(
      this._route.snapshot.queryParamMap.get('returnUrl'),
    );
  }

  initializeAuthStatus(): void {
    const tokens = this.restoreTokens();
    if (tokens) {
      this._session.update((value) => ({
        ...value,
        isAuthenticated: true,
        user: tokens,
      }));
    }
  }

  refreshTokens(): Observable<AuthTokens | null> {
    const tokens = this.restoreTokens();
    if (!tokens?.refreshToken) {
      return of(null);
    }

    if (this._refreshPromise) {
      return from(this._refreshPromise);
    }

    this._refreshPromise = firstValueFrom(
      this._http
        .post<AuthTokens>(
          CORE_AUTH_ENDPOINTS.refreshToken,
          {
            refreshToken: tokens.refreshToken,
          },
          this.ـauthHeaders(),
        )
        .pipe(
          tap((newTokens) => this.saveTokens(newTokens)),
          catchError(() => {
            this.clearTokens();
            return of(null);
          }),
        ),
    ).finally(() => {
      this._refreshPromise = null;
    });

    return from(this._refreshPromise);
  }

  setOtpContext(flow: AuthFlow): void {
    this._session.update((value) => ({
      ...value,
      hasOtpContext: true,
      flow,
    }));
  }

  clearOtpContext(): void {
    this._session.update((value) => ({
      ...value,
      hasOtpContext: false,
      flow: undefined,
    }));
  }

  private _handleSuccessfulAuthentication(tokens: AuthTokenSet): void {
    this.saveTokens(tokens);
    const returnUrl = this.consumeReturnUrl();

    if (returnUrl) {
      this._router.navigateByUrl(returnUrl);
      return;
    }

    this._router.navigate(['/dashboard']);
  }

  private _safeReturnUrl(url: string | null | undefined): string | null {
    if (!url) {
      return null;
    }

    const path = url.split(/[?#]/, 1)[0];

    if (
      !path.startsWith('/') ||
      path.startsWith('//') ||
      path.startsWith('/\\') ||
      path.includes('\\') ||
      path.startsWith('/auth')
    ) {
      return null;
    }

    return path;
  }

  private ـauthHeaders() {
    return {
      headers: {
        clientId: this._appConfig.authClientId,
      },
    };
  }
}
